import { MailCheck, ShieldAlert } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

import { registerOnboardingInterest } from '../api/onboarding';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Spinner } from '../components/ui/spinner';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../context/NotificationProvider';
import { notifyMutationError } from '../lib/errors';

interface BlockedPageProps {
  email: string;
  interestRegistered: boolean;
  onInterestRegistered: () => void;
}

export default function BlockedPage({ email, interestRegistered, onInterestRegistered }: BlockedPageProps) {
  const { logout } = useAuth();
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation({
    mutationFn: registerOnboardingInterest,
    onSuccess: () => {
      showSuccess('Interesse registrado. Avisaremos assim que liberar.');
      onInterestRegistered();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--color-bg-dark)]">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 space-y-6 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-[var(--color-bg-paper)] p-4 border border-[var(--color-divider)]">
              <ShieldAlert className="h-8 w-8 text-[var(--color-primary)]" />
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
              Acesso por convite
            </h1>
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              O JurisApp está em piloto fechado. No momento o uso é mediante convite.
              Se você recebeu um convite, confira se acessou com o email correto.
            </p>
          </div>

          <div className="rounded-lg bg-[var(--color-bg-paper)] border border-[var(--color-divider)] p-4">
            <p className="text-xs text-[var(--color-text-tertiary)] mb-1">Email atual</p>
            <p className="text-sm font-medium text-[var(--color-text-primary)] break-all">{email}</p>
          </div>

          {interestRegistered ? (
            <Alert>
              <MailCheck className="h-4 w-4" />
              <AlertDescription>
                Seu interesse já foi registrado. Avisaremos assim que a conta estiver liberada.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Quer receber um convite quando abrirmos? Registre seu interesse:
              </p>
              <Button
                className="w-full"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                {mutation.isPending && (
                  <Spinner size={16} className="mr-2 text-inherit" />
                )}
                Tenho interesse em um convite
              </Button>
            </div>
          )}

          <div className="pt-2">
            <Button variant="outline" className="w-full" onClick={() => logout()}>
              Sair
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
